import React, { useState } from "react";
import PropTypes from "prop-types";
import submit from "submit-form";

import Modal from "../modal-new";
import { canSwap } from "../../utils/exercise-validations";
import { isCoach } from "../../utils/user-helpers";
import { getPermissionNamesByIds } from "../../views/dashboard/settings/manage-coaches/utils/manage-coaches-utils";
import { SearchDropdown } from "../inputs/search-dropdown";
import { getExerciseName } from "./learn-more/blockHelpers";

const SwapExerciseModal = (props, { locals }) => {
  const [selectedExercise, setSelectedExercise] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const buildMovementOptions = () => {
    const { activeUserToRequest } = locals;
    const { exercise } = props;
    const { exerciseLibrary } = activeUserToRequest;

    const libraryArr = [];

    Object.entries(exerciseLibrary).forEach(([primary, movements]) => {
      if (!["assistant", "other"].includes(primary)) {
        libraryArr.push({
          movementLabel: getExerciseName({
            type: "primary",
            secondary: null,
            primary,
          }),
          value: primary,
          primary,
          type: "primary",
          secondary: null,
          movement: primary[0].toUpperCase() + primary.slice(1),
        });
      }

      movements.forEach((item) => {
        const type = primary === "assistant" ? "assistant" : "secondary";
        const movement = {
          ...item,
          movementLabel: getExerciseName({
            secondary: item.movement,
            primary,
            type,
          }),
          value: `${primary}${item.movement}`,
          primary,
          type,
          secondary: item.movement,
        };
        libraryArr.push(movement);
      });
    });

    const movementOptions = libraryArr.filter((libraryExercise) =>
      canSwap(exercise, libraryExercise)
    );

    return movementOptions;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    const { exercise } = props;
    const { query } = locals;
    if (submitted) return;
    setSubmitted(true);

    if (!confirm("Are you sure you want to swap this exercise?")) {
      return setSubmitted(false);
    }

    if (selectedExercise) {
      const body = {
        typeFrom: exercise.type,
        primaryFrom: exercise.primary,
        secondaryFrom: exercise.secondary,
        typeTo: selectedExercise.type,
        primaryTo: selectedExercise.primary,
        movementTo: selectedExercise.movement,
        startDate: exercise.date,
        proxyAthlete: query.athlete,
      };

      submit("/actions/exercise/swap/", {
        method: "POST",
        body,
      });
    }
  };

  const { exercise } = props;
  const {
    activeUser,
    organizationPermissions: globalOrganizationPermissions = [],
  } = locals;
  const { organizationPermissions = [], organizationId } = activeUser;

  const movementOptions = buildMovementOptions();
  const libraryLink = isCoach(activeUser)
    ? "/coaching-portal/exercise-library"
    : "/dashboard/settings/exercise-library";

  const userOrganizationPermission =
    organizationPermissions.length > 0
      ? getPermissionNamesByIds(
          globalOrganizationPermissions,
          organizationPermissions
        )
      : [""];

  const userOrganizationCanAccessToExerciseLibrary =
    !organizationId ||
    (!!organizationId &&
      userOrganizationPermission.includes("Exercise Library"));

  return (
    <form onSubmit={handleSubmit} id="swap-exercise">
      <Modal
        toClear={["editMode", "exercise"]}
        className="Swap-Exercise"
        title="Swap Exercise"
      >
        <div className="body">
          <p className="p4">Existing exercise</p>

          <p className="s1 mt-1">{getExerciseName(exercise)}</p>

          <label className="mt-6">
            Select exercise that you want to swap with
          </label>

          <SearchDropdown
            options={movementOptions}
            placeholder="Select Exercise"
            valueKey="movementLabel"
            handleChange={setSelectedExercise}
            value={selectedExercise?.movementLabel}
          />

          {selectedExercise ? (
            <div className="chip warning mt-8 items-start">
              <div className="flex flex-col gap-4">
                <span>
                  This will swap all instances of{" "}
                  <span className="font-bold">{getExerciseName(exercise)}</span>{" "}
                  for{" "}
                  <span className="font-bold">
                    {getExerciseName(selectedExercise)}
                  </span>{" "}
                  from this workout forward.
                </span>
                <span>This cannot be undone.</span>
              </div>
            </div>
          ) : (
            <div className="chip info mt-8 items-start">
              <div className="flex flex-col gap-4">
                <span>
                  You can only swap an exercise for an exercise that already
                  exists in your exercise library and that shares the same
                  parameters (e.g. sets, reps, load).
                </span>
                <span>
                  To swap for an exercise that is not on the list, you can
                  create a new one by going to your exercise library.
                </span>
              </div>
            </div>
          )}
        </div>

        <footer className="!justify-between">
          {userOrganizationCanAccessToExerciseLibrary ? (
            <a role="button" data-button-type="secondary" href={libraryLink}>
              Exercise Library
            </a>
          ) : (
            <div className="invisible"></div>
          )}

          <div className="flex gap-2">
            <a
              role="button"
              data-button-type="text-white"
              data-action="cancel"
              href="/actions/refresh?modal[type]=&exercise="
            >
              Cancel
            </a>
            <button role="button" data-button-type="primary" type="submit">
              Swap
            </button>
          </div>
        </footer>
      </Modal>
    </form>
  );
};

SwapExerciseModal.contextTypes = { locals: PropTypes.object };

export default SwapExerciseModal;
